import {
  ActivityProgrammeRow,
  AxisSummary,
  ProgrammeCards,
  StatusSummary,
  TransitionResult,
} from './activityprogramme.models';

const n = (value: any, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const s = (value: any, fallback = ''): string =>
  value === null || value === undefined ? fallback : String(value);

const b = (value: any): boolean =>
  value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';

const list = (value: any): string[] => {
  if (Array.isArray(value)) return value.map(String);
  if (!value) return [];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(String) : [value];
    } catch {
      return value.split(',').map((item) => item.trim()).filter(Boolean);
    }
  }
  return [];
};

export const formatNumber = (value: any) => n(value).toLocaleString();

export const formatMoney = (value: any) => {
  const amount = n(value);
  const abs = Math.abs(amount);
  if (abs >= 1_000_000_000_000) return `${(amount / 1_000_000_000_000).toFixed(2)} T`;
  if (abs >= 1_000_000_000) return `${(amount / 1_000_000_000).toFixed(2)} B`;
  if (abs >= 1_000_000) return `${(amount / 1_000_000).toFixed(2)} M`;
  return amount.toLocaleString();
};

export const formatPercent = (value: any) =>
  value === null || value === undefined || value === '' ? '-' : `${n(value).toFixed(2)}%`;

export const normalizeProgrammeRow = (raw: any, index = 0): ActivityProgrammeRow => {
  const activityplanid = n(raw?.activityplanid ?? raw?.rid);
  const missing = list(raw?.missing ?? raw?.missingfields);
  const taskcount = n(raw?.taskcount);
  const budgetlinecount = n(raw?.budgetlinecount);
  const requiresTaskPlanning = b(raw?.requiresTaskPlanning ?? raw?.requirestaskplanning) || taskcount === 0;

  if (requiresTaskPlanning && !missing.includes('tasks')) missing.push('tasks');

  const plannedamount = n(raw?.plannedamount ?? raw?.coutinitial ?? raw?.initialbudget);
  const revisedamount = n(raw?.revisedamount ?? raw?.coutrevise ?? raw?.revisedbudget ?? plannedamount);
  const varianceamount = n(raw?.varianceamount ?? raw?.ecartamount ?? revisedamount - plannedamount);
  const variancepercent = n(
    raw?.variancepercent ?? raw?.ecartpercent ?? (plannedamount === 0 ? 0 : (varianceamount / plannedamount) * 100),
  );

  const completeness = Math.max(0, Math.min(100, n(raw?.completeness, missing.length === 0 ? 100 : 0)));

  return {
    id: activityplanid || raw?.id || raw?.code || `activity-programme-${index}`,
    rid: activityplanid,
    rix: s(raw?.activityplanix ?? raw?.rix),
    activityplanid,
    activityplanix: s(raw?.activityplanix ?? raw?.rix),
    activityid: n(raw?.activityid),

    code: s(raw?.code ?? raw?.activitycode ?? raw?.shc),
    name: s(raw?.name ?? raw?.activityname ?? raw?.nam),
    description: s(raw?.description ?? raw?.dsc),

    domaine: s(raw?.domaine ?? raw?.axisname),
    resultat: s(raw?.resultat ?? raw?.objectivename ?? raw?.expectedresult),
    indicateur: s(raw?.indicateur ?? raw?.kpiname),
    cible: raw?.cible ?? raw?.targetvalue ?? '',

    t1: s(raw?.t1),
    t2: s(raw?.t2),
    t3: s(raw?.t3),
    t4: s(raw?.t4),

    statut: s(raw?.statut ?? raw?.statename ?? raw?.statecode),
    statecode: s(raw?.statecode ?? raw?.statut).toUpperCase(),
    statename: s(raw?.statename ?? raw?.statut),

    plannedamount,
    revisedamount,
    varianceamount,
    variancepercent,

    commentaires: s(raw?.commentaires ?? raw?.remarks ?? raw?.description),
    responsable: s(raw?.responsable ?? raw?.ownername ?? raw?.staffname),

    axiscode: s(raw?.axiscode),
    axisname: s(raw?.axisname ?? raw?.domaine),
    objectivecode: s(raw?.objectivecode),
    objectivename: s(raw?.objectivename ?? raw?.resultat),
    kpicode: s(raw?.kpicode),
    kpiname: s(raw?.kpiname ?? raw?.indicateur),

    costcenterid: n(raw?.costcenterid),
    costcentercode: s(raw?.costcentercode),
    costcentername: s(raw?.costcentername),

    planningcycleid: n(raw?.planningcycleid),
    cyclecode: s(raw?.cyclecode),
    cyclename: s(raw?.cyclename),
    periodcode: s(raw?.periodcode),
    periodname: s(raw?.periodname),

    taskcount,
    budgetlinecount,
    requiresTaskPlanning,
    missing,
    completeness,

    stamp: s(raw?.stamp ?? raw?.stp),
    raw,
  };
};

export const normalizeProgrammeRows = (raw: any[]): ActivityProgrammeRow[] =>
  (Array.isArray(raw) ? raw : []).map(normalizeProgrammeRow);

export const normalizeProgrammeCards = (raw: any): ProgrammeCards => ({
  totalactivities: n(raw?.totalactivities ?? raw?.activities),
  completeactivities: n(raw?.completeactivities),
  incompleteactivities: n(raw?.incompleteactivities),
  budgetedactivities: n(raw?.budgetedactivities),
  newactivities: n(raw?.newactivities),
  maintainedactivities: n(raw?.maintainedactivities),
  abandonedactivities: n(raw?.abandonedactivities),
  plannedamount: n(raw?.plannedamount ?? raw?.initialbudget),
  revisedamount: n(raw?.revisedamount ?? raw?.revisedbudget),
  varianceamount: n(raw?.varianceamount),
  variancepercent: n(raw?.variancepercent),
  missingfields: n(raw?.missingfields),
  averagetarget: n(raw?.averagetarget),
});

export const normalizeAxisSummary = (raw: any, index = 0): AxisSummary => ({
  id: s(raw?.axiscode ?? raw?.axisname ?? `axis-${index}`),
  axiscode: s(raw?.axiscode),
  axisname: s(raw?.axisname),
  activities: n(raw?.activities),
  completeactivities: n(raw?.completeactivities),
  incompleteactivities: n(raw?.incompleteactivities),
  budgetedactivities: n(raw?.budgetedactivities),
  plannedamount: n(raw?.plannedamount ?? raw?.initialbudget),
  revisedamount: n(raw?.revisedamount ?? raw?.revisedbudget),
  varianceamount: n(raw?.varianceamount),
  variancepercent: n(raw?.variancepercent),
  share: n(raw?.share),
});

export const normalizeStatusSummary = (raw: any, index = 0): StatusSummary => ({
  id: s(raw?.statuscode ?? raw?.statusname ?? `status-${index}`),
  statuscode: s(raw?.statuscode ?? raw?.statut).toUpperCase(),
  statusname: s(raw?.statusname ?? raw?.statut),
  activities: n(raw?.activities),
  plannedamount: n(raw?.plannedamount ?? raw?.initialbudget),
  revisedamount: n(raw?.revisedamount ?? raw?.revisedbudget),
  share: n(raw?.share),
});

export const normalizeTransition = (raw: any): TransitionResult => ({
  success: b(raw?.success ?? raw?.processed),
  rid: n(raw?.rid),
  action: s(raw?.action),
  statecode: s(raw?.statecode),
  stamp: s(raw?.stamp ?? raw?.stp),
  message: s(raw?.message),
  issues: Array.isArray(raw?.issues) ? raw.issues : [],
  processed: n(raw?.processed),
  failed: n(raw?.failed),
  results: Array.isArray(raw?.results) ? raw.results : [],
});
